import React, { useState, useEffect } from 'react'
import { Box, Button, Grid, Link, MenuItem, Switch, TextField, Typography, Alert, LinearProgress } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useSnackbar } from 'notistack'
import PageTitle from '../../../components/PageTitle'
import { Pill, SectionCard, ActionIconBtn, helperProps } from '../components/common'
import DataTable from '../components/Tables'
import PricingRailsCard from '../components/PricingRailsCard'
import { FH_BANDS, FH_GUARDRAILS } from '../mockData'
import { fmtDateTime } from '../apiMap'
import { openS3Doc } from '../s3Doc'
import {
  useGetConfigQuery, useUpdateConfigMutation,
  useGetRateCardLibraryQuery, useAddRateCardLibraryMutation, useDeleteRateCardLibraryMutation, useUploadRateCardLibraryMutation,
  useGetUsersQuery,
} from '../../../redux/d2cApiSlice'

const LIB_SERVICES = ['FWD_SURFACE', 'FWD_AIR', 'SDD_NDD', 'RVP']

export default function FHRules() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: config, isLoading, isError, error } = useGetConfigQuery()
  const [updateConfig, updateState] = useUpdateConfigMutation()
  const [guardrails, setGuardrails] = useState(FH_GUARDRAILS)

  // Rate-card library (v2 §9) + users (v2 §10) — real data.
  const { data: library = [] } = useGetRateCardLibraryQuery()
  const [addLib, addLibState] = useAddRateCardLibraryMutation()
  const [deleteLib] = useDeleteRateCardLibraryMutation()
  const [uploadLib, uploadLibState] = useUploadRateCardLibraryMutation()
  const { data: users = [] } = useGetUsersQuery({})
  const [libForm, setLibForm] = useState({ name: '', serviceType: 'FWD_SURFACE', description: '' })
  const [libFile, setLibFile] = useState(null)
  const [libNameErr, setLibNameErr] = useState(false)
  const libBusy = addLibState.isLoading || uploadLibState.isLoading
  const addLibrary = async () => {
    if (!libForm.name.trim()) { setLibNameErr(true); return }
    try {
      let fileUri = ''
      if (libFile) { const fd = new FormData(); fd.append('file', libFile); const up = await uploadLib(fd).unwrap(); fileUri = up?.fileUri || '' }
      await addLib({ ...libForm, fileUri }).unwrap()
      enqueueSnackbar('Library card added.', { variant: 'success' })
      setLibForm({ name: '', serviceType: 'FWD_SURFACE', description: '' }); setLibFile(null)
    } catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Add failed (Functional-Head only).', { variant: 'error' }) }
  }
  const removeLibrary = async (r) => {
    try { await deleteLib(r.id).unwrap(); enqueueSnackbar('Library card deleted.', { variant: 'info' }) }
    catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Delete failed.', { variant: 'error' }) }
  }

  // Editable config fields (hydrated from GET /config).
  const [r1, setR1] = useState('')
  const [r2, setR2] = useState('')
  const [l1Sla, setL1Sla] = useState('')
  const [guardPct, setGuardPct] = useState('')
  const [guardFlat, setGuardFlat] = useState('')
  const [reviewAll, setReviewAll] = useState('Off')
  // TC-FH-07: config stores bucket names and the engine now resolves buckets from
  // config — expose the rename here ("bucket names editable by the Functional Head").
  const [bucketNames, setBucketNames] = useState([])

  useEffect(() => {
    if (!config) return
    setR1(config.route1MarginGt ?? '')
    setR2(config.route2MarginGte ?? '')
    setL1Sla(config.l1SlaMinutes ?? config.approvalLevels?.find((a) => a.level === 'L1')?.slaMinutes ?? '')
    setGuardPct(config.laneGuardrail?.pct ?? '')
    setGuardFlat(config.laneGuardrail?.flatInr ?? '')
    setReviewAll(config.fhReviewEverything ? 'On' : 'Off')
    // Courier guardrails now persist in the config (BUG-10); the named defaults seed
    // the table only until the first save writes them to the config document.
    if (config.courierGuardrails?.length) {
      setGuardrails(config.courierGuardrails.map((g) => ({ name: g.name, rule: g.rule, on: !!g.on })))
    }
    if (config.buckets?.length) {
      setBucketNames(config.buckets.map((b) => b.name || ''))
    }
  }, [config])

  const save = async () => {
    if (!config) return
    try {
      await updateConfig({
        ...config,
        route1MarginGt: Number(r1),
        route2MarginGte: Number(r2),
        l1SlaMinutes: Number(l1Sla),
        laneGuardrail: { ...config.laneGuardrail, pct: Number(guardPct), flatInr: Number(guardFlat) },
        fhReviewEverything: reviewAll === 'On',
        courierGuardrails: guardrails,
        // TC-FH-07: persist renamed bucket labels (bounds/colours unchanged).
        buckets: config.buckets?.length
          ? config.buckets.map((b, i) => ({ ...b, name: (bucketNames[i] || '').trim() || b.name }))
          : config.buckets,
      }).unwrap()
      enqueueSnackbar('Config saved — applies to every new run and re-band.', { variant: 'success' })
    } catch (e) { enqueueSnackbar(e?.message || 'Could not save config.', { variant: 'error' }) }
  }

  // Per-user HubSpot rule (Hard Stop 1) — persists immediately to config.hubspotRequiredUserIds.
  const toggleHubspotRequired = async (user) => {
    if (!config) return
    const cur = config.hubspotRequiredUserIds || []
    const next = cur.includes(user.id) ? cur.filter((id) => id !== user.id) : [...cur, user.id]
    try {
      await updateConfig({ ...config, hubspotRequiredUserIds: next }).unwrap()
      enqueueSnackbar(`HubSpot deal ID is now ${next.includes(user.id) ? 'REQUIRED' : 'optional'} for ${user.fullName || user.username} — applies to new proposals.`, { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Could not save the HubSpot rule.', { variant: 'error' })
    }
  }

  // Toggles persist immediately to the config document (BUG-10 — they were UI-only).
  const toggleGuard = async (i) => {
    const prev = guardrails
    const next = guardrails.map((g, idx) => (idx === i ? { ...g, on: !g.on } : g))
    setGuardrails(next)
    if (!config) return
    try {
      await updateConfig({ ...config, courierGuardrails: next }).unwrap()
      enqueueSnackbar(`"${next[i].name}" ${next[i].on ? 'enabled' : 'disabled'} — saved.`, { variant: 'success' })
    } catch (e) {
      setGuardrails(prev) // roll back the switch on failure
      enqueueSnackbar(e?.data?.message || e?.message || 'Could not save the guardrail toggle.', { variant: 'error' })
    }
  }

  // Bands derived from the live config thresholds (fallback to representative).
  const bands = config ? [
    { band: 'Route 1', bandKey: 'STRONG', threshold: `> ${config.route1MarginGt}%`, routing: 'L1 approval mandatory · FH notified' },
    { band: 'Route 2', bandKey: 'MODERATE', threshold: `${config.route2MarginGte}–${config.route1MarginGt}%`, routing: 'L1 approval mandatory' },
    { band: 'Route 3', bandKey: 'LOSS', threshold: `< ${config.route2MarginGte}%`, routing: 'FH approval mandatory · L1 rebuilds · order-level RFI gate' },
  ] : FH_BANDS

  const bandCols = [
    { key: 'band', label: 'Band', render: (r) => <Pill label={r.band} bucketKey={r.bandKey} /> },
    { key: 'threshold', label: 'Threshold' },
    { key: 'routing', label: 'Routing' },
  ]
  const guardCols = [
    { key: 'name', label: 'Guardrail', render: (r) => <b>{r.name}</b> },
    { key: 'rule', label: 'Rule' },
    { key: 'on', label: 'Enabled', align: 'right', render: (r) => <Switch size="small" checked={r.on} onChange={() => toggleGuard(guardrails.indexOf(r))} /> },
  ]
  const libCols = [
    { key: 'name', label: 'Card', render: (r) => <b>{r.name}</b> },
    { key: 'serviceType', label: 'Service' },
    { key: 'description', label: 'Description' },
    { key: 'file', label: 'File', render: (r) => (r.fileUri ? <Link component="button" underline="hover" sx={{ fontSize: 12, whiteSpace: 'nowrap' }} onClick={() => openS3Doc(r.fileUri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Open ↗</Link> : '—') },
    { key: 'updatedAt', label: 'Updated', render: (r) => fmtDateTime(r.updatedAt) },
    { key: 'a', label: 'Action', align: 'right', render: (r) => <ActionIconBtn title="Delete" color="error" onClick={() => removeLibrary(r)}><DeleteOutlineIcon fontSize="small" /></ActionIconBtn> },
  ]
  const userCols = [
    { key: 'fullName', label: 'User', render: (r) => <b>{r.fullName || r.username || '—'}</b> },
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'd2cRoles', label: 'D2C Roles', render: (r) => (r.d2cRoles || []).join(', ') || '—' },
    {
      key: 'hubspotRequired',
      label: 'HubSpot ID required',
      align: 'right',
      // Hard Stop 1 is per-user ("set per user by the Functional Head") — only SL
      // (creator) users can create proposals, so the toggle applies to them.
      render: (r) => ((r.d2cRoles || []).includes('SL')
        ? <Switch size="small" checked={(config?.hubspotRequiredUserIds || []).includes(r.id)} onChange={() => toggleHubspotRequired(r)} />
        : <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>—</Typography>),
    },
  ]

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>Rules, Library &amp; Routing</PageTitle>
      {isLoading && <LinearProgress sx={{ my: 1 }} />}
      {isError && <Alert severity="error" sx={{ my: 1 }}>{error?.message || 'Failed to load config.'}</Alert>}

      <SectionCard
        title="Margin Bands & Guardrails"
        subtitle="Routing runs on overall deal margin. Edit the thresholds here — they apply to every new run and every re-band. Margin numbers are masked for sales leaders everywhere."
        action={<Button variant="contained" size="small" sx={{ textTransform: 'none', borderRadius: '6px' }} disabled={!config || updateState.isLoading} onClick={save}>{updateState.isLoading ? 'Saving…' : 'Save changes'}</Button>}
      >
        <DataTable columns={bandCols} rows={bands} getRowKey={(r) => r.band} />
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="Route 1 margin >" value={r1} onChange={(e) => setR1(e.target.value)} /></Grid>
          <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="Route 2 margin ≥" value={r2} onChange={(e) => setR2(e.target.value)} /></Grid>
          <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="Lane guardrail %" value={guardPct} onChange={(e) => setGuardPct(e.target.value)} /></Grid>
          <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="Lane guardrail ₹" value={guardFlat} onChange={(e) => setGuardFlat(e.target.value)} /></Grid>
          <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" label="L1 review SLA (min)" value={l1Sla} onChange={(e) => setL1Sla(e.target.value)} /></Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select fullWidth size="small" label="Review every working" value={reviewAll} onChange={(e) => setReviewAll(e.target.value)}>
              <MenuItem value="Off">Off</MenuItem>
              <MenuItem value="On">On</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        {!!config?.buckets?.length && (
          <>
            <Typography sx={{ fontSize: 12, fontWeight: 700, mt: 2, mb: 0.5 }}>
              Bucket names <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: 'text.secondary' }}>— editable; saved with “Save changes”. Renames apply to every new run; chip colours follow the configured bands.</Typography>
            </Typography>
            <Grid container spacing={1.5}>
              {config.buckets.map((b, i) => (
                <Grid item xs={6} sm={4} md={2.4} key={b.color || i}>
                  <TextField fullWidth size="small" label={`Band ${i + 1}`} value={bucketNames[i] ?? ''} onChange={(e) => setBucketNames((p) => p.map((n, idx) => (idx === i ? e.target.value : n)))} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </SectionCard>

      <SectionCard
        title="Courier guardrails"
        subtitle="Toggles persist in the pricing config. Note: breach-based routing (guardrail breach → L1 even above 20%) is enforced by the pricing engine and is being wired by the backend team."
      >
        <DataTable columns={guardCols} rows={guardrails} getRowKey={(r) => r.name} />
      </SectionCard>

      <PricingRailsCard />

      <SectionCard title="Rate Card Library" subtitle="Versioned library cards (RVP typically quotes as a function of the FWD surface card).">
        <Grid container spacing={1.5} sx={{ mb: 1.5 }} alignItems="center">
          <Grid item xs={12} md={3}><TextField fullWidth size="small" required label="Card name" value={libForm.name} onChange={(e) => { setLibNameErr(false); setLibForm((f) => ({ ...f, name: e.target.value })) }} error={libNameErr} {...helperProps(libNameErr ? 'Card name is required.' : undefined)} /></Grid>
          <Grid item xs={6} md={2}><TextField select fullWidth size="small" label="Service" value={libForm.serviceType} onChange={(e) => setLibForm((f) => ({ ...f, serviceType: e.target.value }))}>{LIB_SERVICES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Description" value={libForm.description} onChange={(e) => setLibForm((f) => ({ ...f, description: e.target.value }))} /></Grid>
          <Grid item xs={6} md={2}>
            <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none', borderRadius: '6px' }}>{libFile ? '📎 file ✓' : '📎 File'}<input type="file" hidden accept=".xlsx,.pdf" onChange={(e) => setLibFile(e.target.files?.[0] || null)} /></Button>
          </Grid>
          <Grid item xs={6} md={1}><Button variant="contained" size="small" sx={{ textTransform: 'none', borderRadius: '6px' }} disabled={libBusy} onClick={addLibrary}>{libBusy ? '…' : 'Add'}</Button></Grid>
        </Grid>
        <DataTable columns={libCols} rows={library} getRowKey={(r) => r.id} emptyText="No library cards yet" />
      </SectionCard>

      <SectionCard title="Users, Routing & Notifications" subtitle="D2C users and their roles. Workspace visibility is controlled by the access flags on each user's profile.">
        <DataTable columns={userCols} rows={users} getRowKey={(r) => r.id} emptyText="No D2C users" />
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
          Tip: visibility of each workspace is controlled by the access flags <b>PROPOSAL_CREATE</b>, <b>PROPOSAL_L1</b> and <b>PROPOSAL_ADMIN</b> on the user's profile in User &amp; Access.
        </Typography>
      </SectionCard>
    </Box>
  )
}
